import { IAction } from 'types';
import { SagaIterator } from 'redux-saga';
import { put, call, select } from 'typed-redux-saga';
import { getOperation, getOperationData } from 'selectors';
import { mergeDeep } from 'utils';
import { operationsActions as A } from './operations-reducer-actions';
import {
    Service, bindActionsTyped, leading, onStart, every
} from '../service-utils';

export class OperationsService extends Service {
    static serviceName = 'OperationsService';
    static generatedStopActionTypes: string[] = [];
    static generatedWatchers: Function[] = [];

    static* safeCreate(id: string):SagaIterator {
        const _operation = yield* select(getOperation, id);
        if (!_operation) {
            yield* put(A.init(id));
        }
    }

    static* setMeta(id: string, meta: any):SagaIterator {
        const _operation = yield* select(getOperation, id);
        if (_operation) {
            yield* put(A.setMeta({ id, meta }));
        }
    }

    static* setData(id: string, data: any):SagaIterator {
        const _operation = yield* select(getOperation, id);
        if (_operation) {
            yield* put(A.setData({ id, data }));
        }
    }

    static* updateData(id: string, data: any):SagaIterator {
        const _operation = yield* select(getOperation, id);
        if (_operation) {
            const mergedData = mergeDeep(_operation.data, data);
            yield* put(A.setData({ id, data: mergedData }));
        }
    }

    static* pending(id: string):SagaIterator {
        yield* call(OperationsService.safeCreate, id);
        yield* put(A.pending(id));
    }

    static* success(id: string):SagaIterator {
        yield* call(OperationsService.safeCreate, id);
        yield* put(A.success(id));
    }

    static* fail(id: string, error?: any, error_message?: string):SagaIterator {
        yield* call(OperationsService.safeCreate, id);
        yield* put(A.fail({ id, error, error_message }));
    }

    static* operation(id: string, subSaga, args: any[] = [], meta?: any) {
        yield* call(OperationsService.safeCreate, id);
        if (meta) {
            yield* put(A.setMeta({ id, meta }));
        }
        yield* put(A.pending(id));
        try {
            yield* call(subSaga, ...args);
            yield* put(A.success(id));
        } catch (e) {
            const error = e.isAxiosError ? e.response : e;
            yield* put(A.fail({ id, error }));
        }
    }

    @leading
    static* clearOperation(action: IAction<string>) {
        yield* put(A.clear(action.payload));
    }
}

export function operation(
    id: string,
    raiseError: boolean = false,
    error_message?: string
) {
    return function decorator(target, name: string, descriptor: PropertyDescriptor): PropertyDescriptor {
        const original = descriptor.value;
        descriptor.value = function* (...args) {
            yield* call(OperationsService.safeCreate, id);
            yield* put(A.pending(id));
            let data;
            try {
                data = yield* call(original, ...args);
                if (data) {
                    yield* put(A.setData({ id, data }));
                }
                yield* put(A.success(id));
            } catch (e) {
                console.error(e);
                const error = e.isAxiosError ? e.response : e;
                yield* put(A.fail({ id, error, error_message }));
                if (raiseError) {
                    throw e;
                }
            }
            return data;
        };
        return descriptor;
    };
}

export const operationsService = new OperationsService();
export const actions = bindActionsTyped(OperationsService, operationsService);
